<script>
  import Button from '$lib/components/Button.svelte';
  import Icon from '$lib/components/Icon.svelte';
  import Select from '$lib/components/form/Select.svelte';
  import { showToast } from '$lib/features/app/modals';
  import {
    getVideoMeeting,
    useInsertVideoEventHistory,
  } from '$lib/features/video-meeting/services';
  import { logger } from '$lib/logger';
  import DailyIframe from '@daily-co/daily-js';
  import { createQuery } from '@tanstack/svelte-query';
  import { onDestroy, onMount, untrack } from 'svelte';
  import { twMerge } from 'tailwind-merge';
  import Transcription from '../../transcription/components/Transcription.svelte';
  const insertVideoEventHistory = useInsertVideoEventHistory();

  /**
   * @typedef {Object} Props
   * @property {any} meetingId
   * @property {any} event
   * @property {boolean} [meetingStarted]
   */

  /** @type {Props} */
  let {
    meetingId = $bindable(),
    event,
    meetingStarted = $bindable(false),
  } = $props();

  //  Transcription variables
  let clientId = $state(undefined);
  let meetingTranscription = $state({});
  let isTranscriptionVisible = $state(false);
  let transcriptionStatus = $state('idle');
  let meetingConducted = $state(false);
  let transcriptionLanguage = $state('sv'); // Default to Swedish
  let previousLanguage = $state('sv'); // Track previous language for cancel functionality
  let showLanguageSelector = $state(false);
  let clearTranscriptionData = $state(false);

  $effect.pre(() => {
    if (event) {
      if (event.data) {
        clientId = event.data.clientId;
      }
    }
  });

  // Supported transcription languages (common healthcare languages)
  const transcriptionLanguageOptions = [
    { label: 'Svenska', value: 'sv' },
    { label: 'Engelska', value: 'en' },
    { label: 'Danska', value: 'da' },
    { label: 'Norska', value: 'no' },
    { label: 'Finska', value: 'fi' },
  ];

  // Supported language codes for validation
  const SUPPORTED_LANGUAGES = transcriptionLanguageOptions.map(
    (opt) => opt.value,
  );

  let myVideoElement = $state();
  let clientVideoElement = $state();
  let clientAudioElement = $state();

  let eventIsFinished = $state(false);
  let isJoining = $state(false);
  let isClientInLobby = $state(false);

  let muted = $state(false);
  let cameraOff = $state(false);

  let callObject;
  let client = $state();
  let me = $state();
  let isLeavingCall = false; // Guard against double-leave

  const reset = () => {
    meetingStarted = false;
    meetingConducted = false;
    eventIsFinished = false;
    isJoining = false;
    me = null;
    client = null;
  };

  // Helper to get or create call object safely
  // Prevents "Duplicate DailyIframe instances are not allowed" error
  const getCallObject = async () => {
    // Check if we already have a valid instance
    if (callObject && !callObject.isDestroyed()) {
      return callObject;
    }

    // Check for existing global instance
    const existingInstance = DailyIframe.getCallInstance();
    if (existingInstance && !existingInstance.isDestroyed()) {
      const state = existingInstance.meetingState?.();
      if (state === 'left-meeting' || state === 'error') {
        // Don't reuse a stale instance — await destroy before creating fresh
        try {
          await existingInstance.destroy();
        } catch {
          // Ignore destroy errors — instance may already be partially destroyed
        }
      } else {
        callObject = existingInstance;
        return existingInstance;
      }
    }

    // Only create new instance if none exists or stale one was destroyed
    const newInstance = DailyIframe.createCallObject({
      dailyConfig: { avoidEval: true },
    });
    callObject = newInstance;
    return newInstance;
  };

  // Attach/detach my video stream
  $effect(() => {
    if (myVideoElement) {
      const myVideoTrack = me?.tracks?.video?.persistentTrack;

      if (myVideoTrack) {
        myVideoElement.srcObject = new MediaStream([myVideoTrack]);
      } else {
        myVideoElement.srcObject = null;
      }
    }
  });

  // Attach/detach client video stream
  $effect(() => {
    if (clientVideoElement) {
      const clientVideoTrack = client?.tracks?.video?.persistentTrack;

      if (clientVideoTrack) {
        clientVideoElement.srcObject = new MediaStream([clientVideoTrack]);
      } else {
        clientVideoElement.srcObject = null;
      }
    }
  });

  // Attach/detach client audio stream
  $effect(() => {
    if (clientAudioElement) {
      const clientAudioTrack = client?.tracks?.audio?.persistentTrack;

      if (clientAudioTrack) {
        clientAudioElement.srcObject = new MediaStream([clientAudioTrack]);
      } else {
        clientAudioElement.srcObject = null;
      }
    }
  });

  // const, not $derived: createQuery reads the query-client context and so can
  // only run during init. Safe here because VideoSidebar mounts this inside
  // {#key $activeCalendarEvent.id} — one mount per meeting.
  const meetingDetails = createQuery({
    queryKey: ['video-meeting', meetingId],
    queryFn: () => getVideoMeeting(meetingId),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // v5 removed query-level onSuccess/onError, so the outcome is handled here.
  // The latches are required: reset() clears `me`/`client`/`meetingStarted`, and a
  // bare reactive `if` would re-run on every store emission and kill a live call.
  // VideoSidebar mounts this under {#key event.id}, so one mount == one meeting
  // == at most one fetch outcome, which makes a boolean the right granularity.
  let didHandleSuccess = $state(false);
  let didHandleError = $state(false);

  $effect(() => {
    if (!didHandleSuccess && $meetingDetails.isSuccess) {
      didHandleSuccess = true;
      // untrack: reset() writes me/client/meetingStarted/eventIsFinished, and
      // an effect tracks state read inside functions it calls.
      untrack(() => reset());
    }
  });

  $effect(() => {
    if (!didHandleError && $meetingDetails.isError) {
      didHandleError = true;
      const error = $meetingDetails.error;
      logger.error('Failed to join video meeting', {
        err: error instanceof Error ? error : undefined,
      });
      if (error?.message === 'Event has ended') {
        eventIsFinished = true;
        showToast('Eventet är redan avslutat', 'error');
      }
    }
  });

  const clientLeft = (event) => {
    if (!event.participant.local) {
      showToast(event.participant.user_name + ' lämnade mötet', 'info');
      client = null;
      meetingStarted = false;
      meetingConducted = true;
    }
  };
  const clientJoined = (event) => {
    if (event.participant.local) {
      // I joined
      me = event.participant;
    } else {
      // Patient joined
      client = event.participant;
    }
  };

  const clientUpdated = (event) => {
    if (event.participant.local) {
      // I was updated
      me = event.participant;
    } else {
      // Patient was updated
      client = event.participant;
    }
  };

  const pictureInPicture = async () => {
    try {
      await clientVideoElement.requestPictureInPicture();
    } catch {
      showToast('Kunde inte aktivera bild i bild', 'error');
    }
  };

  const joiningMeeting = () => {
    isJoining = true;
  };

  const joinedMeeting = async () => {
    isJoining = false;
    meetingStarted = true;
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'MEETING_STARTED',
        metadata: {
          initiatedBy: 'nurse',
        },
      },
    });

    await initializeTranscription();
  };

  const stopTranscription = async () => {
    try {
      await callObject.stopTranscription();

      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'TRANSCRIPTION_STOPPED',
          metadata: {
            initiatedBy: 'nurse',
          },
        },
      });

      if (transcriptionStatus !== 'done') {
        transcriptionStatus = 'finished';
      }
    } catch (e) {
      const errorMessage = e?.message || 'Unknown error';
      showToast('Kunde inte stoppa transkribering', 'error');
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'TRANSCRIPTION_STOP_FAILED',
          metadata: {
            error: errorMessage,
            initiatedBy: 'nurse',
          },
        },
      });
      throw e; // Re-throw for caller to handle
    }
  };

  const startTranscription = async () => {
    try {
      if (transcriptionStatus !== 'recording') {
        // Validate language code before API call
        if (!SUPPORTED_LANGUAGES.includes(transcriptionLanguage)) {
          const errorMessage = `Unsupported language: ${transcriptionLanguage}`;
          showToast('Ogiltigt språk för transkribering', 'error');
          await $insertVideoEventHistory.mutateAsync({
            eventId: meetingId,
            data: {
              statusMessage: 'TRANSCRIPTION_START_FAILED',
              metadata: {
                error: errorMessage,
                initiatedBy: 'nurse',
              },
            },
          });
          return;
        }

        await callObject.startTranscription({
          language: transcriptionLanguage,
          model: 'nova-2',
        });
        transcriptionStatus = 'recording';
        await $insertVideoEventHistory.mutateAsync({
          eventId: meetingId,
          data: {
            statusMessage: 'TRANSCRIPTION_STARTED',
            metadata: {
              initiatedBy: 'nurse',
              language: transcriptionLanguage,
            },
          },
        });
      }
    } catch (e) {
      const errorMessage = e?.message || 'Unknown error';
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'TRANSCRIPTION_START_FAILED',
          metadata: {
            error: errorMessage,
            language: transcriptionLanguage,
            initiatedBy: 'nurse',
          },
        },
      });
      showToast(`Kunde inte starta transkribering: ${errorMessage}`, 'error');
      throw e; // Re-throw for caller to handle
    }
  };

  const clientWaiting = async () => {
    isClientInLobby = true;
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'CLIENT_WAITING',
        metadata: {
          initiatedBy: 'nurse',
        },
      },
    });
  };

  const handleError = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'ERROR',
        metadata: {
          action: event?.action,
          error: event?.errorMsg,
          initiatedBy: 'nurse',
        },
      },
    });
    showToast('Ett allvarligt fel inträffade i videosamtalet', 'error');
  };

  const handleNonFatalError = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'NONFATAL_ERROR',
        metadata: {
          type: event?.type,
          error: event?.errorMsg,
          details: event?.details,
          initiatedBy: 'nurse',
        },
      },
    });

    // Show user-friendly message for certain error types
    if (event?.type === 'input-settings-error') {
      showToast('Problem med ljud- eller videoinställningar', 'error');
    }
  };

  const handleNetworkQualityChange = async (event) => {
    const networkState = event?.networkState;

    if (networkState !== 'good') {
      // Log low network quality event
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'NETWORK_QUALITY_CHANGE',
          metadata: {
            networkState,
            initiatedBy: 'nurse',
          },
        },
      });
    }

    // Warn nurse if network quality is poor
    if (networkState === 'warning') {
      showToast(
        'Mycket dålig nätverkskvalitet - videosamtalet kan bli instabilt',
        'error',
      );
    } else if (networkState === 'bad') {
      showToast(
        'Låg nätverkskvalitet - videokvaliteten kan påverkas',
        'warning',
      );
    }
  };

  const handleNetworkConnection = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'NETWORK_CONNECTION',
        metadata: {
          type: event?.type,
          event: event?.event,
          session_id: event?.session_id,
          initiatedBy: 'nurse',
        },
      },
    });

    // Notify nurse of connection interruptions
    if (event?.event === 'interrupted') {
      showToast('Anslutningen avbröts - återansluter...', 'error');
    } else if (event?.event === 'connected' && event.type !== 'signaling') {
      showToast('Anslutningen återställd', 'success');
    }
  };

  const handleTrackStarted = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'TRACK_STARTED',
        metadata: {
          participant: event?.participant?.session_id,
          type: event?.track?.kind,
          initiatedBy: 'nurse',
        },
      },
    });
  };

  const handleTrackStopped = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'TRACK_STOPPED',
        metadata: {
          participant: event?.participant?.session_id,
          type: event?.track?.kind,
          initiatedBy: 'nurse',
        },
      },
    });
  };

  const handleCameraError = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'CAMERA_ERROR',
        metadata: {
          error: event?.error,
          initiatedBy: 'nurse',
        },
      },
    });

    const errorType = event?.error?.type;
    if (errorType === 'permissions') {
      showToast(
        'Kameraåtkomst nekad - kontrollera webbläsarinställningar',
        'error',
      );
    } else if (errorType === 'cam-in-use' || errorType === 'cam-mic-in-use') {
      showToast('Kameran används av en annan applikation', 'error');
    } else {
      showToast('Kamerafel inträffade', 'error');
    }
  };

  const handleInputSettingsUpdated = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'INPUT_SETTINGS_UPDATED',
        metadata: {
          action: event?.action,
          inputSettings: event?.inputSettings,
          initiatedBy: 'nurse',
        },
      },
    });
  };

  const handleAvailableDevicesUpdated = async (event) => {
    await $insertVideoEventHistory.mutateAsync({
      eventId: meetingId,
      data: {
        statusMessage: 'AVAILABLE_DEVICES_UPDATED',
        metadata: {
          action: event?.action,
          availableDevices: event?.availableDevices,
          initiatedBy: 'nurse',
        },
      },
    });
  };

  const newTranscriptionHandle = (event) => {
    const participantId = event.participantId;
    if (!participantId) return;
    // Drop transcription events from unknown participants (defense-in-depth)
    const sessionIds = [me?.session_id, client?.session_id].filter(Boolean);
    if (!sessionIds.includes(participantId)) return;
    const isNurse = participantId === me?.session_id;
    onNewTranscriptionMessage({ ...event, isNurse });
  };

  function onNewTranscriptionMessage(event) {
    if (!event) return;

    meetingTranscription = {
      participantId: event.participantId,
      isNurse: event.isNurse,
      text: event.text,
      timestamp: event.timestamp,
      callClientId: event.callClientId,
    };
  }

  onDestroy(() => {
    clientId = null;
    meetingId = null;
    meetingTranscription = {};
  });

  onMount(() => {
    let mounted = true;

    // Get or create call object safely to prevent duplicate instances
    // NOTE: getCallObject is async (may need to await destroy of stale instance),
    // but Svelte's onMount cleanup only works with sync return values.
    // We use a mounted flag to guard against the component being destroyed
    // before getCallObject resolves.
    getCallObject().then((co) => {
      if (!mounted) {
        // Component was destroyed before getCallObject resolved
        co.destroy().catch(() => {});
        return;
      }

      callObject = co;

      // Core meeting events
      callObject.on('joining-meeting', joiningMeeting);
      callObject.on('joined-meeting', joinedMeeting);
      callObject.on('participant-joined', clientJoined);
      callObject.on('participant-updated', clientUpdated);
      callObject.on('participant-left', clientLeft);
      callObject.on('waiting-participant-added', clientWaiting);

      // Transcription events
      callObject.on('transcription-message', newTranscriptionHandle);

      // Error events
      callObject.on('error', handleError);
      callObject.on('nonfatal-error', handleNonFatalError);
      callObject.on('camera-error', handleCameraError);

      // Network and connection events
      callObject.on('network-quality-change', handleNetworkQualityChange);
      callObject.on('network-connection', handleNetworkConnection);

      // Track events for diagnostics
      callObject.on('track-started', handleTrackStarted);
      callObject.on('track-stopped', handleTrackStopped);

      // Device events
      callObject.on('input-settings-updated', handleInputSettingsUpdated);
      callObject.on('available-devices-updated', handleAvailableDevicesUpdated);
    });

    return () => {
      mounted = false;
      if (!callObject || callObject.isDestroyed()) return;

      // Remove all event listeners
      callObject.off('joining-meeting', joiningMeeting);
      callObject.off('joined-meeting', joinedMeeting);
      callObject.off('participant-joined', clientJoined);
      callObject.off('participant-updated', clientUpdated);
      callObject.off('participant-left', clientLeft);
      callObject.off('waiting-participant-added', clientWaiting);
      callObject.off('transcription-message', newTranscriptionHandle);
      callObject.off('error', handleError);
      callObject.off('nonfatal-error', handleNonFatalError);
      callObject.off('camera-error', handleCameraError);
      callObject.off('network-quality-change', handleNetworkQualityChange);
      callObject.off('network-connection', handleNetworkConnection);
      callObject.off('track-started', handleTrackStarted);
      callObject.off('track-stopped', handleTrackStopped);
      callObject.off('input-settings-updated', handleInputSettingsUpdated);
      callObject.off(
        'available-devices-updated',
        handleAvailableDevicesUpdated,
      );

      // Leave call and cleanup
      leaveCall().then(() => {
        if (callObject && !callObject.isDestroyed()) {
          callObject.destroy().catch((err) => {
            logger.error('Error destroying call object:', {
              err: err instanceof Error ? err : undefined,
            });
          });
        }
      });
    };
  });

  const startMeetingForPatient = async () => {
    try {
      if (!callObject) return;
      // The patient is waiting in the lobby until this event
      // await callObject.sendAppMessage({ action: 'START_MEETING_FOR_PATIENT' })
      const waiting = callObject.waitingParticipants();
      const waitList = Object.keys(waiting);
      waitList.forEach((id) => {
        callObject.updateWaitingParticipant(id, {
          grantRequestedAccess: true,
        });
      });
      isClientInLobby = false;
    } catch {
      showToast('Kunde inte starta mötet för patienten', 'error');
    }
  };

  const joinCall = async () => {
    // Validate meeting details exist
    if (!$meetingDetails.data) {
      showToast(
        'Kunde inte ansluta till mötet. Försök igen om en liten stund.',
        'error',
      );
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'MEETING_JOIN_FAILED',
          metadata: {
            error: 'Meeting details not available',
            initiatedBy: 'nurse',
          },
        },
      });
      return;
    }

    const { room, roomToken } = $meetingDetails.data;

    // Validate room URL and token BEFORE logging as joined
    if (!room?.url || !roomToken) {
      showToast('Kunde inte ansluta till mötet - mötesdata saknas', 'error');
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'MEETING_JOIN_FAILED',
          metadata: {
            error: 'Room URL or token missing',
            initiatedBy: 'nurse',
          },
        },
      });
      return;
    }

    let joinSucceeded = false;

    try {
      // Attempt to join the meeting
      await callObject.join({
        url: room.url,
        token: roomToken,
        startVideoOff: cameraOff,
        startAudioOff: muted,
      });
      joinSucceeded = true;

      // Log successful join AFTER it actually succeeds
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'MEETING_JOINED',
          dailycoRoomName: room.name,
          metadata: {
            initiatedBy: 'nurse',
          },
        },
      });

      meetingStarted = true;

      // Try to initialize transcription
      try {
        await initializeTranscription();
      } catch (transcriptionError) {
        // Log transcription failure but don't fail the join
        await $insertVideoEventHistory.mutateAsync({
          eventId: meetingId,
          data: {
            statusMessage: 'TRANSCRIPTION_INIT_FAILED',
            metadata: {
              error: transcriptionError.message,
              initiatedBy: 'nurse',
            },
          },
        });
        showToast('Transkribering kunde inte startas', 'warning');
      }
    } catch (e) {
      const errorMessage = e?.message || 'Unknown error';

      // Log the join failure
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'MEETING_JOIN_FAILED',
          metadata: {
            error: errorMessage,
            joinSucceeded,
            initiatedBy: 'nurse',
          },
        },
      });

      showToast(`Kunde inte ansluta till mötet: ${errorMessage}`, 'error');

      // If join succeeded but something else failed, leave the meeting
      if (joinSucceeded) {
        try {
          await callObject.leave();
        } catch (leaveError) {
          logger.error('Error leaving meeting after failed join:', {
            err: leaveError instanceof Error ? leaveError : undefined,
          });
        }
      }

      // Reset state
      meetingStarted = false;
      isJoining = false;
    }
  };

  const leaveCall = async () => {
    // Guard against double-calling
    if (isLeavingCall) {
      return;
    }
    isLeavingCall = true;

    try {
      // Only stop transcription if we're actually in a meeting
      if (meetingStarted && transcriptionStatus !== 'done') {
        try {
          await stopTranscription();
        } catch (transcriptionError) {
          logger.error('Error stopping transcription:', {
            err:
              transcriptionError instanceof Error
                ? transcriptionError
                : undefined,
          });
          // Continue with leave even if transcription stop fails
        }
      }

      // Only leave if we're actually in a meeting
      const meetingState = callObject?.meetingState?.();
      if (
        meetingState &&
        meetingState !== 'left-meeting' &&
        meetingState !== 'error'
      ) {
        await callObject.leave();

        await $insertVideoEventHistory.mutateAsync({
          eventId: meetingId,
          data: {
            statusMessage: 'NURSE_LEFT_MEETING',
            metadata: {
              initiatedBy: 'nurse',
            },
          },
        });
      }
    } catch (error) {
      logger.error('Error in leaveCall:', {
        err: error instanceof Error ? error : undefined,
      });
      await $insertVideoEventHistory.mutateAsync({
        eventId: meetingId,
        data: {
          statusMessage: 'LEAVE_CALL_ERROR',
          metadata: {
            error: error?.message,
            initiatedBy: 'nurse',
          },
        },
      });
    } finally {
      reset();
      isLeavingCall = false;
    }
  };

  const toggleMuted = () => {
    muted = !muted;
    callObject?.setLocalAudio(!muted);
  };

  const toggleCamera = () => {
    cameraOff = !cameraOff;
    callObject?.setLocalVideo(!cameraOff);
  };
  const toggleTranscription = () => {
    isTranscriptionVisible = !isTranscriptionVisible;
  };

  const handleLanguageChange = async () => {
    // Check if language actually changed
    if (transcriptionLanguage === previousLanguage) {
      showToast('Språket är redan valt', 'info');
      return;
    }

    const selectedLanguageLabel = transcriptionLanguageOptions.find(
      (opt) => opt.value === transcriptionLanguage,
    )?.label;

    // If transcription is currently recording, restart it with the new language
    if (transcriptionStatus === 'recording') {
      try {
        await stopTranscription();
        // Small delay to ensure stop is processed
        setTimeout(async () => {
          // Clear existing transcription data since we're changing languages
          // This ensures the new language transcription replaces the old one
          clearTranscriptionData = true;
          meetingTranscription = {};
          await startTranscription();
          previousLanguage = transcriptionLanguage; // Update previous language
          showToast(
            `Transkribering bytt till ${selectedLanguageLabel}`,
            'success',
          );
        }, 500);
      } catch {
        showToast('Kunde inte byta språk för transkribering', 'error');
      }
    } else {
      // If transcription is not recording, just show confirmation
      previousLanguage = transcriptionLanguage; // Update previous language
      showToast(
        `Språk ändrat till ${selectedLanguageLabel}. Används vid nästa transkribering.`,
        'info',
      );
    }
  };

  const toggleLanguageSelector = () => {
    showLanguageSelector = !showLanguageSelector;
  };

  const cancelLanguageChange = () => {
    transcriptionLanguage = previousLanguage; // Reset to previous language
    toggleLanguageSelector(); // Close the dropdown
  };

  const handleLanguageChangeAndClose = async () => {
    await handleLanguageChange();
    toggleLanguageSelector(); // Close the dropdown after changing
  };

  const handleTranscriptionCleared = () => {
    // Reset the clearTranscriptionData flag after the event is handled
    clearTranscriptionData = false;
  };

  // Initialize transcription - extracted common logic
  const initializeTranscription = async () => {
    try {
      await startTranscription();
    } catch {
      showToast('Kunde inte initiera transkribering', 'error');
    }
  };
</script>

<div class="relative">
  <div
    class=" relative mb-2 h-full w-full overflow-hidden rounded-sm border border-gray-8 bg-gray-9"
  >
    {#if clientId && meetingId}
      <Transcription
        isShowTranscription={isTranscriptionVisible}
        data={meetingTranscription}
        {clientId}
        calendarEventId={meetingId}
        bind:transcriptionStatus
        bind:clearTranscription={clearTranscriptionData}
        ontranscriptionCleared={handleTranscriptionCleared}
      />
    {/if}
    {#if eventIsFinished}
      <div class="absolute inset-0 z-10 flex items-center justify-center">
        <div class="text-shadow text-lg text-white">
          Mötet är redan avslutat.
        </div>
      </div>
    {:else if !meetingStarted}
      <div class="absolute inset-0 z-10 flex items-center justify-center">
        <Button onclick={joinCall} class="w-40" loading={isJoining}>
          Gå med i mötet
        </Button>
      </div>
    {:else if isClientInLobby}
      <div class="absolute inset-0 z-10 flex items-center justify-center">
        <div>
          <div class="text-shadow mb-2 text-center text-lg text-white">
            Patienten väntar i lobbyn.
          </div>
          <Button onclick={startMeetingForPatient}>
            Starta mötet för patienten
          </Button>
        </div>
      </div>
    {:else if !client}
      <div class="absolute inset-0 z-10 flex items-center justify-center">
        <div class="text-shadow text-lg text-white">
          Väntar på att patienten ska ansluta.
        </div>
      </div>
    {/if}

    <div class="relative transform">
      <video
        class="h-full max-h-[40vh] w-full"
        bind:this={clientVideoElement}
        height={500}
        width={500}
        autoplay
        playsinline
      >
        <track kind="captions" />
      </video>
    </div>

    <div class="absolute right-2 top-2 flex gap-2 text-white">
      {#if client && !client?.audio}
        <Icon icon="mic_off" size="lg" />
      {/if}
      {#if client && !client?.video}
        <Icon icon="videocam_off" size="lg" />
      {/if}
    </div>
    <div
      class={twMerge(
        'absolute inset-0 h-full w-full overflow-hidden rounded-sm transition-all duration-300',
        client &&
          'bottom-4 left-4 right-auto top-auto h-auto w-32 border-gray-7',
      )}
    >
      <div class="-scale-x-100 transform bg-gray-8">
        <video
          class="h-full w-full object-cover"
          bind:this={myVideoElement}
          height={500}
          width={500}
          autoplay
          playsinline
        >
          <track kind="captions" />
        </video>
      </div>
      <div class="absolute right-2 top-2 flex gap-4 text-red-400">
        {#if muted}
          <Icon icon="mic_off" size="lg" />
        {/if}
        {#if cameraOff}
          <Icon icon="videocam_off" size="lg" />
        {/if}
      </div>
    </div>
  </div>

  <audio autoPlay playsInline bind:this={clientAudioElement}></audio>

  <div class="flex justify-center gap-4">
    <button
      class={twMerge(
        'text-700 flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 transition',
        (meetingStarted || meetingConducted) &&
          'bg-red text-gray-2 hover:bg-red-400 hover:text-white',
      )}
      onclick={leaveCall}
    >
      <Icon icon="call_end" size="lg" />
    </button>
    {#if muted}
      <button
        class="flex h-12 w-12 items-center justify-center rounded-full bg-red text-gray-2 transition hover:bg-red-400 hover:text-white"
        onclick={toggleMuted}
      >
        <Icon icon="mic_off" size="lg" />
      </button>
    {:else}
      <button
        class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 text-gray-2 transition hover:bg-gray-8"
        onclick={toggleMuted}
      >
        <Icon icon="mic" size="lg" />
      </button>
    {/if}

    {#if cameraOff}
      <button
        class="flex h-12 w-12 items-center justify-center rounded-full bg-red text-gray-2 transition hover:bg-red-400 hover:text-white"
        onclick={toggleCamera}
      >
        <Icon icon="videocam_off" size="lg" />
      </button>
    {:else}
      <button
        class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 text-gray-2 transition hover:bg-gray-8"
        onclick={toggleCamera}
      >
        <Icon icon="videocam" size="lg" />
      </button>
    {/if}

    <button
      onclick={pictureInPicture}
      class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 text-gray-2 transition hover:bg-gray-8"
    >
      <Icon icon="picture_in_picture" size="lg" />
    </button>
    <button
      onclick={toggleTranscription}
      class={twMerge(
        'relative flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 text-gray-2 transition hover:bg-gray-8',
        isTranscriptionVisible && 'bg-white text-gray-9 hover:bg-gray-1 ',
      )}
    >
      {#if transcriptionStatus !== 'idle'}
        <div class="absolute right-0 top-0 flex h-4 w-4">
          <div
            class={twMerge(
              'absolute inline-flex h-full w-full rounded-full  opacity-75',
              transcriptionStatus === 'recording' && 'animate-ping bg-red-500',
              transcriptionStatus === 'saving' && 'animate-ping bg-yellow-500',
              transcriptionStatus === 'done' && ' bg-green-500',
              transcriptionStatus === 'summarizing' &&
                'animate-ping bg-purple-500',
            )}
          ></div>
          <div
            class={twMerge(
              'relative inline-flex h-4 w-4 rounded-full',
              transcriptionStatus === 'recording' && 'bg-red-500',
              transcriptionStatus === 'saving' && ' bg-yellow-500',
              transcriptionStatus === 'done' && ' bg-green-500',
              transcriptionStatus === 'summarizing' && 'bg-purple-500',
            )}
          ></div>
        </div>
      {/if}
      <div class="flex justify-end">
        <Icon icon="comments" />
      </div>
    </button>

    <button
      onclick={toggleLanguageSelector}
      class="relative flex h-12 w-12 items-center justify-center rounded-full bg-gray-9 text-gray-2 transition hover:bg-gray-8"
      title="Välj transkriberingsspråk"
    >
      <Icon icon="globe" size="lg" />
    </button>
  </div>

  <!-- Language Selector Dropdown -->
  {#if showLanguageSelector}
    <div class="mt-4 flex justify-center">
      <div class="w-64 rounded-md bg-white p-4 shadow-lg dark:bg-gray-8">
        <h3 class="mb-3 text-sm font-medium text-gray-3">
          Transkriberingsspråk
          <span class="text-xs font-normal text-gray-6">
            (nuvarande: {transcriptionLanguageOptions.find(
              (opt) => opt.value === previousLanguage,
            )?.label})
          </span>
        </h3>
        <Select
          bind:value={transcriptionLanguage}
          options={transcriptionLanguageOptions}
          placeholder="Välj språk"
          class="mb-3"
        />
        {#if transcriptionStatus === 'recording'}
          <p class="text-xs text-gray-6">
            Språkbyte kommer att starta om transkriberingen
          </p>
        {/if}
        <div class="mt-3 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onclick={cancelLanguageChange}>
            Avbryt
          </Button>
          <Button size="sm" onclick={handleLanguageChangeAndClose}>
            Byt språk
          </Button>
        </div>
      </div>
    </div>
  {/if}
</div>
